import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Envelope, Lock, ArrowRight, ShieldCheck, QrCode, Key, Copy, Check, ArrowLeft } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_aquatic-crm-sync/artifacts/d0yhpcoq_PoolFIX%20-%20Logo%20-%20RGB%20350px.jpg";

function formatError(detail) {
  if (!detail) return "Er ging iets mis";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// ── 6-digit OTP input (auto-advance, paste friendly) ─────────────────────────
function OtpInput({ value, onChange, disabled, testIdPrefix = "otp" }) {
  const inputs = useRef([]);
  const chars = (value || '').padEnd(6, ' ').slice(0, 6).split('');

  const setAt = (idx, ch) => {
    const arr = (value || '').padEnd(6, ' ').slice(0, 6).split('');
    arr[idx] = ch || ' ';
    const next = arr.join('').replace(/ /g, '').slice(0, 6);
    onChange(next);
  };

  const handleChange = (idx, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) { setAt(idx, ''); return; }
    if (raw.length > 1) {
      // paste case
      const next = raw.slice(0, 6);
      onChange(next);
      const focusIdx = Math.min(next.length, 5);
      inputs.current[focusIdx]?.focus();
      return;
    }
    setAt(idx, raw);
    if (idx < 5) inputs.current[idx + 1]?.focus();
  };

  const handleKey = (idx, e) => {
    if (e.key === 'Backspace' && !chars[idx].trim() && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center" data-testid={`${testIdPrefix}-input`}>
      {chars.map((c, i) => (
        <input
          key={i}
          ref={el => inputs.current[i] = el}
          data-testid={`${testIdPrefix}-digit-${i}`}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={c.trim()}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          disabled={disabled}
          className="w-11 h-12 text-center text-lg font-data font-bold border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A] disabled:bg-zinc-50"
        />
      ))}
    </div>
  );
}

// ── "Trust device 30 days" checkbox ─────────────────────────────────────────
function TrustDeviceCheckbox({ checked, onChange, disabled, testId = "trust-device-checkbox" }) {
  return (
    <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none">
      <input
        data-testid={testId}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        disabled={disabled}
        className="w-4 h-4 rounded-sm border-zinc-300 text-[#0F172A] focus:ring-1 focus:ring-[#0F172A] cursor-pointer"
      />
      Vertrouw dit apparaat <strong className="text-zinc-800">30 dagen</strong> — sla 2FA over op deze browser
    </label>
  );
}

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // stage: password | challenge | enrol_qr | enrol_confirm | recovery
  const [stage, setStage] = useState('password');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated — but NEVER during the 'recovery' stage,
  // otherwise newly-enrolled users miss their one-time recovery codes.
  useEffect(() => {
    if (user && stage !== 'recovery') navigate('/dashboard', { replace: true });
     
  }, [user, stage]);

  // password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // 2FA state
  const [tempToken, setTempToken] = useState('');
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');

  // Enrolment state
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);

  // Recovery codes after first enable
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [rcCopied, setRcCopied] = useState(false);

  // Bumped on error to force-remount OtpInput (fully clears digit boxes)
  const [otpResetKey, setOtpResetKey] = useState(0);

  // "Trust this device 30 days" preference (sent to backend on verify/enable)
  const [trustDevice, setTrustDevice] = useState(false);

  useEffect(() => {
    if (stage === 'challenge' && code.length === 6 && !loading && !useRecovery) {
      handleVerify();
    }
     
  }, [code, stage]);

  useEffect(() => {
    if (stage === 'enrol_confirm' && code.length === 6 && !loading) {
      handleEnable();
    }
     
  }, [code, stage]);

  const resetToPassword = () => {
    setStage('password');
    setTempToken('');
    setCode('');
    setRecoveryCode('');
    setUseRecovery(false);
    setQrDataUrl('');
    setSecret('');
    setError('');
  };

  const submitInFlight = useRef(false);

  const parseJson = async (res) => {
    // Defensive: emergent-main.js wraps fetch and consumes response.text() on non-OK
    // responses (see assets.emergent.sh/scripts/emergent-main.js). When that happens
    // res.bodyUsed is true and clone()/text()/json() all throw. We swallow that and
    // return {} — callers fall back to status-code-based messages via fallbackError().
    let r;
    try { r = res.clone(); } catch { return {}; }
    let txt = '';
    try { txt = await r.text(); } catch { return {}; }
    if (!txt) return {};
    try { return JSON.parse(txt); } catch { return { _raw: txt }; }
  };

  // Map HTTP status to a user-facing Dutch message for each auth endpoint.
  const fallbackError = (status, kind) => {
    if (kind === 'login') {
      if (status === 401) return 'Ongeldig emailadres of wachtwoord';
      if (status === 403) return 'Registratie is uitgeschakeld';
      if (status === 429) return 'Te veel pogingen — probeer over 15 minuten opnieuw';
      if (status === 400) return 'Email en wachtwoord zijn vereist';
    }
    if (kind === 'verify') {
      if (status === 401) return 'Ongeldige code';
      if (status === 429) return 'Te veel pogingen — probeer over 15 minuten opnieuw';
      if (status === 400) return 'Code en token zijn vereist';
    }
    if (kind === 'enable') {
      if (status === 400) return 'Ongeldige code — probeer opnieuw';
    }
    if (kind === 'setup') {
      if (status === 401) return 'Sessie verlopen — log opnieuw in';
    }
    return 'Er ging iets mis';
  };

  const showApiError = (data, status, kind) => {
    if (data?.detail) setError(formatError(data.detail));
    else setError(fallbackError(status, kind));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await parseJson(res);
      if (!res.ok) {
        showApiError(data, res.status, 'login');
        setLoading(false);
        submitInFlight.current = false;
        return;
      }
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setStage('challenge');
        setLoading(false);
        submitInFlight.current = false;
        return;
      }
      if (data.requires_2fa_setup) {
        setTempToken(data.temp_token);
        // Directly fetch QR
        await fetchSetupQr(data.temp_token);
        setLoading(false);
        submitInFlight.current = false;
        return;
      }
      // Legacy: directe login (zou niet meer voorkomen)
      login(data);
      navigate('/dashboard', { replace: true });
      submitInFlight.current = false;
    } catch {
      setError('Verbindingsfout. Probeer het opnieuw.');
      setLoading(false);
      submitInFlight.current = false;
    }
  };

  const fetchSetupQr = async (token) => {
    try {
      const res = await fetch(`${API}/api/auth/2fa/setup`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: token }),
      });
      const data = await parseJson(res);
      if (!res.ok) {
        showApiError(data, res.status, 'setup');
        return;
      }
      setQrDataUrl(data.qr_data_url);
      setSecret(data.secret);
      setStage('enrol_qr');
    } catch {
      setError('Kon QR-code niet laden.');
    }
  };

  const handleEnable = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/2fa/enable`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, code, trust_device: trustDevice }),
      });
      const data = await parseJson(res);
      if (!res.ok) {
        showApiError(data, res.status, 'enable');
        setCode('');
        setOtpResetKey(k => k + 1);
        setLoading(false);
        return;
      }
      // Succes → toon recovery codes, dan dashboard
      setRecoveryCodes(data.recovery_codes || []);
      login(data.user);
      setStage('recovery');
    } catch {
      setError('Verbindingsfout bij activeren.');
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    setError('');
    setLoading(true);
    const submitCode = useRecovery ? recoveryCode.trim() : code;
    try {
      const res = await fetch(`${API}/api/auth/2fa/verify`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, code: submitCode, trust_device: trustDevice }),
      });
      const data = await parseJson(res);
      if (!res.ok) {
        showApiError(data, res.status, 'verify');
        setCode('');
        setRecoveryCode('');
        setOtpResetKey(k => k + 1);
        setLoading(false);
        return;
      }
      login(data);
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Verbindingsfout.');
    }
    setLoading(false);
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const copyRecovery = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setRcCopied(true);
      setTimeout(() => setRcCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const downloadRecovery = () => {
    const blob = new Blob(
      [`PoolFIX — 2FA Recovery Codes\nAccount: ${email}\nGegenereerd: ${new Date().toISOString()}\n\n${recoveryCodes.join('\n')}\n\nBewaar deze codes op een veilige plek. Elke code werkt één keer.`],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `poolfix-recovery-codes-${email}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─────────────────── UI per stage ───────────────────
  const headerTitle = {
    password: 'Inloggen',
    challenge: 'Tweestapsverificatie',
    enrol_qr: '2FA instellen',
    enrol_confirm: 'Bevestig met code',
    recovery: 'Bewaar je herstelcodes',
  }[stage];

  const headerSub = {
    password: 'Toegang tot je PoolFIX dashboard',
    challenge: `Voer de 6-cijferige code in uit je authenticator-app voor ${email}`,
    enrol_qr: 'Scan de QR-code met Google Authenticator, Microsoft Authenticator of Authy',
    enrol_confirm: 'Voer de 6-cijferige code uit je app in om te bevestigen',
    recovery: 'Bewaar deze codes nu — ze worden maar één keer getoond',
  }[stage];

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://images.pexels.com/photos/3413096/pexels-photo-3413096.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F172A]/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="PoolFIX" className="h-9 w-auto" />
          </div>
          <div>
            <h1 className="font-heading text-4xl font-black tracking-tight mb-4">European Pool<br />Lead Generator</h1>
            <p className="text-zinc-300 text-base max-w-md leading-relaxed">
              Identify, verify, and export swimming pool leads across Europe.
              Built for B2B sales teams targeting the aquatic sector.
            </p>
          </div>
          <div className="flex gap-8 text-sm">
            <div><div className="font-data text-2xl font-bold text-[#0055FF]">4</div><div className="text-zinc-400 mt-1">Landen</div></div>
            <div><div className="font-data text-2xl font-bold text-[#0055FF]">1000+</div><div className="text-zinc-400 mt-1">Faciliteiten</div></div>
            <div><div className="font-data text-2xl font-bold text-[#0055FF]">2FA</div><div className="text-zinc-400 mt-1">Beveiligd</div></div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-white">
        <div className="w-full max-w-sm" data-testid={`login-stage-${stage}`}>
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src={LOGO_URL} alt="PoolFIX" className="h-8 w-auto" />
          </div>

          {/* Back-to-password link voor 2FA stages */}
          {stage !== 'password' && stage !== 'recovery' && (
            <button
              type="button"
              onClick={resetToPassword}
              data-testid="back-to-login-btn"
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 mb-4 transition-colors"
            >
              <ArrowLeft size={14} /> Terug naar inloggen
            </button>
          )}

          <h2 className="font-heading text-2xl font-bold tracking-tight mb-1" data-testid="stage-title">
            {headerTitle}
          </h2>
          <p className="text-sm text-zinc-500 mb-6">{headerSub}</p>

          {error && (
            <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          {/* ── Stage: password ── */}
          {stage === 'password' && (
            <>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    data-testid="email-input"
                    type="email" placeholder="Emailadres" value={email}
                    onChange={e => setEmail(e.target.value)} required
                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-400"
                  />
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    data-testid="password-input"
                    type="password" placeholder="Wachtwoord" value={password}
                    onChange={e => setPassword(e.target.value)} required minLength={6}
                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-400"
                  />
                </div>
                <button
                  data-testid="auth-submit-btn"
                  type="submit" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-sm font-medium rounded-sm hover:bg-[#1E293B] disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Bezig...' : 'Inloggen'}
                  <ArrowRight size={14} />
                </button>
              </form>

              <div className="text-center mt-3">
                <a
                  href="/forgot-password"
                  className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Wachtwoord vergeten?
                </a>
              </div>

              <p className="text-xs text-zinc-400 mt-6 text-center leading-relaxed" data-testid="register-disabled-note">
                Registratie is uitgeschakeld — alleen geautoriseerde gebruikers hebben toegang.<br />
                Alle dataverwerking is AVG/GDPR-conform.
              </p>
            </>
          )}

          {/* ── Stage: challenge (verify) ── */}
          {stage === 'challenge' && (
            <div className="space-y-5">
              <div className="flex justify-center">
                <div className="w-12 h-12 rounded-full bg-[#0055FF]/10 flex items-center justify-center">
                  <ShieldCheck size={22} className="text-[#0055FF]" />
                </div>
              </div>

              {!useRecovery ? (
                <>
                  <OtpInput key={`v-${otpResetKey}`} value={code} onChange={setCode} disabled={loading} testIdPrefix="totp" />
                  <TrustDeviceCheckbox checked={trustDevice} onChange={setTrustDevice} disabled={loading} />
                  <button
                    type="button"
                    data-testid="verify-code-btn"
                    onClick={handleVerify}
                    disabled={loading || code.length !== 6}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-sm font-medium rounded-sm hover:bg-[#1E293B] disabled:opacity-40 transition-colors"
                  >
                    {loading ? 'Bezig...' : 'Verifiëren'}
                  </button>
                  <button
                    type="button"
                    data-testid="use-recovery-btn"
                    onClick={() => { setUseRecovery(true); setCode(''); setError(''); }}
                    className="w-full text-xs text-zinc-500 hover:text-zinc-800 flex items-center justify-center gap-1 transition-colors"
                  >
                    <Key size={12} /> Gebruik een herstelcode
                  </button>
                  <a
                    href="/forgot-2fa"
                    data-testid="forgot-2fa-link"
                    className="block w-full text-center text-xs text-zinc-400 hover:text-zinc-700 transition-colors"
                  >
                    Authenticator kwijt? 2FA resetten via e-mail
                  </a>
                </>
              ) : (
                <>
                  <input
                    data-testid="recovery-code-input"
                    type="text"
                    placeholder="xxxx-xxxx"
                    value={recoveryCode}
                    onChange={e => setRecoveryCode(e.target.value)}
                    className="w-full px-4 py-2.5 text-center text-sm font-data tracking-wider border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A]"
                  />
                  <TrustDeviceCheckbox checked={trustDevice} onChange={setTrustDevice} disabled={loading} testId="trust-device-checkbox-recovery" />
                  <button
                    type="button"
                    data-testid="verify-recovery-btn"
                    onClick={handleVerify}
                    disabled={loading || recoveryCode.length < 9}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-sm font-medium rounded-sm hover:bg-[#1E293B] disabled:opacity-40 transition-colors"
                  >
                    {loading ? 'Bezig...' : 'Herstelcode gebruiken'}
                  </button>
                  <button
                    type="button"
                    data-testid="back-to-totp-btn"
                    onClick={() => { setUseRecovery(false); setRecoveryCode(''); setError(''); }}
                    className="w-full text-xs text-zinc-500 hover:text-zinc-800 transition-colors"
                  >
                    Gebruik toch de authenticator-code
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── Stage: enrol_qr ── */}
          {stage === 'enrol_qr' && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-sm text-xs text-blue-900 flex gap-2">
                <QrCode size={16} className="flex-shrink-0 mt-0.5" />
                <div>
                  Beveiliging vereist. Scan de QR-code in je authenticator-app of voer het geheim handmatig in.
                </div>
              </div>

              {qrDataUrl && (
                <div className="flex justify-center">
                  <div className="p-3 bg-white border border-zinc-200 rounded-sm">
                    <img src={qrDataUrl} alt="TOTP QR Code" className="w-48 h-48" data-testid="totp-qr-image" />
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-[11px] uppercase tracking-wider text-zinc-500">Handmatig geheim</label>
                <div className="flex gap-2">
                  <code
                    className="flex-1 px-3 py-2 text-xs font-data bg-zinc-50 border border-zinc-200 rounded-sm break-all"
                    data-testid="totp-secret-display"
                  >
                    {secret}
                  </code>
                  <button
                    type="button"
                    onClick={copySecret}
                    data-testid="copy-secret-btn"
                    className="px-3 py-2 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors flex items-center gap-1"
                  >
                    {secretCopied ? <><Check size={12} /> Gekopieerd</> : <><Copy size={12} /> Kopieer</>}
                  </button>
                </div>
              </div>

              <button
                type="button"
                data-testid="continue-to-confirm-btn"
                onClick={() => { setCode(''); setError(''); setStage('enrol_confirm'); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-sm font-medium rounded-sm hover:bg-[#1E293B] transition-colors"
              >
                Ik heb de code toegevoegd <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* ── Stage: enrol_confirm ── */}
          {stage === 'enrol_confirm' && (
            <div className="space-y-5">
              <div className="flex justify-center">
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                  <Key size={22} className="text-emerald-600" />
                </div>
              </div>
              <OtpInput key={`e-${otpResetKey}`} value={code} onChange={setCode} disabled={loading} testIdPrefix="enrol-totp" />
              <TrustDeviceCheckbox checked={trustDevice} onChange={setTrustDevice} disabled={loading} testId="trust-device-checkbox-enrol" />
              <button
                type="button"
                data-testid="enable-2fa-btn"
                onClick={handleEnable}
                disabled={loading || code.length !== 6}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-sm font-medium rounded-sm hover:bg-[#1E293B] disabled:opacity-40 transition-colors"
              >
                {loading ? 'Bezig...' : 'Activeer 2FA'}
              </button>
              <button
                type="button"
                data-testid="back-to-qr-btn"
                onClick={() => { setStage('enrol_qr'); setCode(''); setError(''); }}
                className="w-full text-xs text-zinc-500 hover:text-zinc-800 flex items-center justify-center gap-1 transition-colors"
              >
                <ArrowLeft size={12} /> QR-code opnieuw bekijken
              </button>
            </div>
          )}

          {/* ── Stage: recovery codes ── */}
          {stage === 'recovery' && (
            <div className="space-y-4" data-testid="recovery-codes-panel">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900">
                Bewaar deze 8 codes op een veilige plek. Elke code werkt <strong>één keer</strong> en
                kan worden gebruikt als je geen toegang hebt tot je authenticator-app.
              </div>

              <div className="grid grid-cols-2 gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                {recoveryCodes.map((c, i) => (
                  <code
                    key={i}
                    data-testid={`recovery-code-${i}`}
                    className="text-xs font-data text-zinc-800 tracking-wider"
                  >
                    {c}
                  </code>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={copyRecovery}
                  data-testid="copy-recovery-btn"
                  className="flex-1 px-3 py-2 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors flex items-center justify-center gap-1"
                >
                  {rcCopied ? <><Check size={12} /> Gekopieerd</> : <><Copy size={12} /> Kopieer alles</>}
                </button>
                <button
                  type="button"
                  onClick={downloadRecovery}
                  data-testid="download-recovery-btn"
                  className="flex-1 px-3 py-2 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors"
                >
                  Download .txt
                </button>
              </div>

              <button
                type="button"
                data-testid="finish-recovery-btn"
                onClick={() => navigate('/dashboard', { replace: true })}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-sm font-medium rounded-sm hover:bg-[#1E293B] transition-colors"
              >
                Ik heb ze bewaard — ga naar dashboard <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
